import { useMemo } from 'react'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import { api, type PortfolioCalendar } from '@/api/client'
import {
  mergePortfolioWindows,
  type PortfolioProperty,
} from '@/components/portfolio-calendar/merge'
import { windowRange, type WindowSpan } from '@/components/portfolio-calendar/windows'

/**
 * The portfolio calendar read (`GET /admin/calendar`, admin-api CAP-1414),
 * 21 days at a time as the grid scrolls (CAP-1416).
 *
 * Every key leads with `['portfolio-calendar']`, so anything that changes what
 * a property's calendar shows (a block made or released, a cleaning edited)
 * invalidates every window read with that one prefix: the grid only ever shows
 * what the API returned, never an optimistic edit.
 *
 * One query per window rather than one `useInfiniteQuery`, on purpose:
 * - a window is fetched, cached, refreshed and retried on its own. An infinite
 *   query refetches all of its pages one after another and shows the result
 *   only when the last has arrived, so a refresh on focus, or a block made from
 *   the grid, would wait 1.4 to 2.2 s per window read so far before showing;
 * - a failed window fails alone, with a retry for those days, while its
 *   neighbours stay on screen;
 * - windows far from the screen are dropped wherever they are, not only from
 *   the far end, and are read again when the grid comes back to them.
 * The window math an infinite query's `getNextPageParam` and
 * `getPreviousPageParam` would hold is in `windows.ts`: the next window is
 * `n + 1`, the previous `n - 1`.
 */
export function portfolioCalendarKey() {
  return ['portfolio-calendar'] as const
}

/** How long a window's read is fresh: the app's usual 30 s. */
export const PORTFOLIO_STALE_MS = 30_000

export function portfolioWindowQuery(window: number) {
  const { from, to } = windowRange(window)
  return {
    queryKey: [...portfolioCalendarKey(), from, to] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      api.getPortfolioCalendar({ from, to }, { signal }),
    staleTime: PORTFOLIO_STALE_MS,
    // The app turns this off by default; a calendar left open in a tab must
    // catch up with what changed while it was away.
    refetchOnWindowFocus: true,
  }
}

export interface PortfolioWindowState {
  window: number
  from: string
  to: string
  /** What the window read last, kept on screen while it refreshes or after a refresh fails. */
  data: PortfolioCalendar | undefined
  /** When `data` was read (TanStack's `dataUpdatedAt`): a later read is the fresher. */
  updatedAt: number
  /** No answer yet: drawn as the loading skeleton. */
  isPending: boolean
  /** The last read failed: with no `data`, its days show an inline retry. */
  error: Error | null
  isFetching: boolean
  refetch: () => void
}

export interface PortfolioWindows {
  /** Every window the grid spans, in day order. */
  windows: PortfolioWindowState[]
  /** Every property across the windows read, merged by item id (`merge.ts`). */
  properties: PortfolioProperty[]
  /** Whether any window has answered (the grid has rows to draw). */
  hasData: boolean
}

interface UsePortfolioWindowsOptions {
  /** The windows the grid spans: those outside are not observed. */
  span: WindowSpan
  /** The windows on screen: read first. */
  visible: WindowSpan
  /** The windows to read: on screen and within one window of it. */
  wanted: WindowSpan
}

export function usePortfolioWindows({
  span,
  visible,
  wanted,
}: UsePortfolioWindowsOptions): PortfolioWindows {
  const queryClient = useQueryClient()

  // The days on screen are asked for first, alone, so the first paint waits on
  // one read and not on four in parallel; the windows beside them follow once
  // those have answered (or failed). A window in the span but not wanted is
  // observed without being fetched: it shows what the cache holds, if anything.
  const visibleRead = rangeOf(visible).every(window => {
    const state = queryClient.getQueryState(portfolioWindowQuery(window).queryKey)
    return state !== undefined && (state.data !== undefined || state.status === 'error')
  })
  // The same array while nothing about the windows changes: the grid renders
  // once per day scrolled, and TanStack re-derives (and re-hashes) every
  // query's options whenever this array is new.
  const { first: spanFirst, last: spanLast } = span
  const { first: visibleFirst, last: visibleLast } = visible
  const { first: wantedFirst, last: wantedLast } = wanted
  const queries = useMemo(
    () =>
      rangeOf({ first: spanFirst, last: spanLast }).map(window => ({
        ...portfolioWindowQuery(window),
        enabled:
          (window >= visibleFirst && window <= visibleLast) ||
          (visibleRead && window >= wantedFirst && window <= wantedLast),
      })),
    [spanFirst, spanLast, visibleFirst, visibleLast, wantedFirst, wantedLast, visibleRead]
  )
  const results = useQueries({ queries })

  const windows = rangeOf(span).map((window, index): PortfolioWindowState => {
    const result = results[index]
    const { from, to } = windowRange(window)
    return {
      window,
      from,
      to,
      data: result.data,
      updatedAt: result.dataUpdatedAt,
      isPending: result.data === undefined && !result.isError,
      error: result.error,
      isFetching: result.isFetching,
      refetch: () => void result.refetch(),
    }
  })

  // Merged, and then laid out, once per data version: when a window's answer
  // arrives or changes, never because a read started or the grid scrolled.
  const dataKey = results
    .map(
      (result, index) =>
        `${span.first + index}:${result.dataUpdatedAt}:${result.data ? identityOf(result.data) : 0}`
    )
    .join('|')
  const properties = useMemo(
    () =>
      mergePortfolioWindows(
        results.flatMap((result, index) =>
          result.data
            ? [{ window: span.first + index, data: result.data, updatedAt: result.dataUpdatedAt }]
            : []
        )
      ),
    // `dataKey` is the data version: it changes exactly when a window's data does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataKey]
  )

  return { windows, properties, hasData: results.some(result => result.data !== undefined) }
}

// A number per answer object: TanStack keeps an unchanged answer's object, so
// a new number means new data even when two reads land in the same millisecond.
const identities = new WeakMap<object, number>()
let nextIdentity = 1
function identityOf(data: object): number {
  let identity = identities.get(data)
  if (identity === undefined) {
    identity = nextIdentity++
    identities.set(data, identity)
  }
  return identity
}

function rangeOf(span: WindowSpan): number[] {
  const windows: number[] = []
  for (let window = span.first; window <= span.last; window++) windows.push(window)
  return windows
}
